import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/react";
import { Modal } from "@heroui/react";
import { ArrowRight, CalendarPlus, Loader2, Mic, Search, Settings, User, UserPlus, Users, X } from "lucide-react";
import { getGlobalSearch as getGlobalSearchApi } from "@/api/search";
import { cn } from "@/utils/cn";

const DEBOUNCE_MS = 2000;

const QUICK_ACTIONS = [
  { id: "qa-1", label: "New Loop", icon: CalendarPlus, href: "/?create=1" },
  { id: "qa-2", label: "Add Connection", icon: UserPlus, href: "/network?add=1" },
  { id: "qa-3", label: "View All Loops", icon: Users, href: "/loops" },
  { id: "qa-4", label: "Recordings", icon: Mic, href: "/recordings" },
  { id: "qa-5", label: "Settings", icon: Settings, href: "/settings" },
];

// Keyed off relation.type from the search response.
const RELATION_CONFIG = {
  meets: {
    label: "Loop",
    icon: CalendarPlus,
    badge: "bg-accent/10 text-accent",
    to: (id) => `/loops?selected=${id}`,
  },
  user: {
    label: "Connection",
    icon: User,
    badge: "bg-blue/10 text-blue-foreground",
    to: (id) => `/network?selected=${id}`,
  },
  recordings: {
    label: "Recording",
    icon: Mic,
    badge: "bg-purple/10 text-purple-foreground",
    to: (id) => `/recordings?selected=${id}`,
  },
};

const FALLBACK_CONFIG = {
  label: "Result",
  icon: Search,
  badge: "bg-default/10 text-default-500",
  to: () => "/",
};

const getConfig = (type) => RELATION_CONFIG[type] ?? FALLBACK_CONFIG;

const formatCreatedOn = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const ResultRow = ({ item, onSelect }) => {
  const config = getConfig(item.relation?.type);
  const Icon = config.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(config.to(item.relation?.id))}
      className="group flex items-center gap-3 w-full px-3 py-1.5 rounded-lg hover:bg-muted/10 cursor-pointer transition-colors text-left"
    >
      <div className="flex items-center justify-center size-8 rounded-lg bg-default-100 group-hover:bg-background shrink-0 transition-colors">
        <Icon size={15} className="text-default-500" />
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground truncate">{item.text}</span>
        <span className="text-xs text-default-400 truncate">{formatCreatedOn(item.created_on)}</span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span className={cn("text-[10px] font-medium px-1.5 py-0.5 rounded-md", config.badge)}>{config.label}</span>
        <ArrowRight size={13} className="text-default-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
};

const GlobalSearch = ({ placeholder = "Search connections, dates, titles...", className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { session } = useSession();
  const navigate = useNavigate();

  // Guards against a slow earlier request landing after a faster later one.
  const requestIdRef = useRef(0);

  const runSearch = useCallback(
    async (term) => {
      if (!session) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setIsSearching(true);

      try {
        const token = await session.getToken();
        const _resp = await getGlobalSearchApi(term, "full", token);

        if (requestIdRef.current !== requestId) return; // stale

        if (_resp.data.Result === "OK") {
          setResults(_resp.data.Data ?? []);
        } else {
          throw new Error("Search failed");
        }
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        console.error(err);
        setResults([]);
      } finally {
        if (requestIdRef.current === requestId) {
          setIsSearching(false);
          setHasSearched(true);
        }
      }
    },
    [session],
  );

  // Debounce: fire DEBOUNCE_MS after the last keystroke.
  useEffect(() => {
    const term = searchQuery.trim();

    if (!term) {
      requestIdRef.current += 1; // cancel anything in flight
      setResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(false);

    const timer = setTimeout(() => runSearch(term), DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, runSearch]);

  // ⌘K / Ctrl+K to open, matching the hint on the trigger.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
    setResults([]);
    setHasSearched(false);
  };

  const handleSelect = (href) => {
    handleClose();
    if (href) navigate(href);
  };

  const groupedResults = results.reduce((acc, item) => {
    const label = getConfig(item.relation?.type).label;
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {});

  return (
    <>
      <div className={cn("w-full", className)}>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="md:flex hidden items-center gap-2 lg:min-w-100 lg:h-12 h-10 lg:w-full max-w-xs px-4 rounded-3xl shadow-sm bg-surface cursor-text transition-colors text-sm text-default-400"
        >
          <Search size={15} className="shrink-0" />
          <span className="flex-1 text-left truncate text-muted">{placeholder}</span>
          <kbd className="hidden sm:flex items-center gap-0.5 text-[10px] text-default-300 font-mono border border-default-200 rounded px-1 py-0.5">
            ⌘K
          </kbd>
        </button>
      </div>

      <Modal isOpen={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <Modal.Backdrop isDismissable className="bg-black/40 backdrop-blur-sm">
          <Modal.Container className="pt-16 px-4">
            <Modal.Dialog className="w-full max-w-2xl bg-surface rounded-2xl shadow-xl p-0 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-default-100">
                {isSearching ? (
                  <Loader2 size={17} className="text-default-400 shrink-0 animate-spin" />
                ) : (
                  <Search size={17} className="text-default-400 shrink-0" />
                )}

                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={placeholder}
                  className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-default-400"
                />

                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-default-400 hover:text-foreground transition-colors"
                  >
                    <X size={15} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleClose}
                  className="text-[11px] text-default-400 hover:text-foreground border border-default-200 rounded-md px-2 py-0.5 transition-colors"
                >
                  Esc
                </button>
              </div>

              {/* Body */}
              <Modal.Body className="p-3 max-h-[65vh] overflow-y-auto">
                {!searchQuery.trim() ? (
                  <div className="flex flex-col gap-0">
                    <p className="text-[11px] font-semibold text-default-400 uppercase tracking-wide px-3 py-1">
                      Quick Actions
                    </p>

                    {QUICK_ACTIONS.map((action) => {
                      const Icon = action.icon;
                      return (
                        <button
                          key={action.id}
                          type="button"
                          onClick={() => handleSelect(action.href)}
                          className="group flex items-center gap-3 w-full px-3 py-1.5 rounded-lg hover:bg-muted/10 cursor-pointer transition-colors text-left"
                        >
                          <div className="flex items-center justify-center size-7 rounded-md bg-default-100 group-hover:bg-background shrink-0 transition-colors">
                            <Icon size={14} className="text-default-500" />
                          </div>
                          <span className="text-xs font-medium text-foreground">{action.label}</span>
                          <ArrowRight
                            size={13}
                            className="text-default-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </button>
                      );
                    })}
                  </div>
                ) : isSearching ? (
                  <div className="flex flex-col gap-1 py-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-1.5">
                        <div className="size-8 rounded-lg bg-default/40 animate-pulse shrink-0" />
                        <div className="flex flex-col gap-1.5 flex-1">
                          <div className="h-3.5 w-2/5 rounded bg-default/40 animate-pulse" />
                          <div className="h-3 w-1/4 rounded bg-default/30 animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : results.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {Object.entries(groupedResults).map(([label, items]) => (
                      <div key={label} className="flex flex-col gap-0.5">
                        <p className="text-[11px] font-semibold text-default-400 uppercase tracking-wide px-3 py-1">
                          {label}s
                        </p>
                        {items.map((item, i) => (
                          <ResultRow
                            key={`${item.relation?.id ?? "item"}-${i}`}
                            item={item}
                            onSelect={handleSelect}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : hasSearched ? (
                  <div className="flex flex-col items-center gap-2 py-16 text-center">
                    <Search size={36} className="text-default-200" />
                    <p className="text-sm font-medium text-default-500">No results for "{searchQuery}"</p>
                    <p className="text-xs text-default-400">Try a loop title, a person's name, or a date</p>
                  </div>
                ) : null}
              </Modal.Body>

              <div className="flex items-center gap-4 px-4 py-2 border-t border-default-100">
                <span className="text-[11px] text-default-300 flex items-center gap-1">
                  <kbd className="border border-default-200 rounded px-1 font-mono">↑↓</kbd> navigate
                </span>
                <span className="text-[11px] text-default-300 flex items-center gap-1">
                  <kbd className="border border-default-200 rounded px-1 font-mono">↵</kbd> open
                </span>
                <span className="text-[11px] text-default-300 flex items-center gap-1">
                  <kbd className="border border-default-200 rounded px-1 font-mono">Esc</kbd> close
                </span>
              </div>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
};

export default GlobalSearch;